/**
 * Adds mouse click-and-drag scrolling to a native horizontally-scrollable
 * element. Touch and trackpad/wheel scrolling are left completely untouched —
 * this only activates for mouse pointer input, so it's a pure progressive
 * enhancement with no effect until a mouse drag actually starts.
 *
 * Dragging is only committed to once the pointer has moved past a small
 * threshold. Below that threshold nothing is touched at all — no
 * preventDefault, no pointer capture — so a plain click (e.g. on a logo or
 * product card link) reaches its target completely normally. Only once a
 * real drag is confirmed do we take over scrolling and swallow the
 * trailing click so a dragged-past link doesn't accidentally navigate.
 *
 * Used by:
 *  - <drag-scroll> (e.g. the footer logo strip's .scroll-area)
 *  - any element with a [data-drag-scroll] attribute (e.g. <slider-element>
 *    in the Featured collection section)
 */
function enableDragScroll(el) {
  if (!el || el.__dragScrollEnabled) return;
  el.__dragScrollEnabled = true;

  const DRAG_THRESHOLD = 5;

  let isTracking = false; // mouse is down, watching to see if this becomes a drag
  let isDragging = false; // threshold exceeded, actively dragging
  let startX = 0;
  let scrollStart = 0;
  let previousScrollBehavior = '';

  function onPointerDown(event) {
    // Only handle mouse input — touch/pen keep native scrolling untouched.
    if (event.pointerType !== 'mouse') return;
    // Only the primary (left) button — right-click/middle-click stay native.
    if (event.button !== 0) return;

    isTracking = true;
    isDragging = false;
    startX = event.pageX;
    scrollStart = el.scrollLeft;
    // Deliberately no preventDefault/setPointerCapture here yet — see
    // onPointerMove, where dragging is only committed to past the threshold.
  }

  function onPointerMove(event) {
    if (!isTracking) return;

    const distance = event.pageX - startX;

    if (!isDragging) {
      if (Math.abs(distance) < DRAG_THRESHOLD) return;

      // Threshold crossed — commit to dragging now, not before.
      isDragging = true;
      el.classList.add('dragging');

      // Some sliders use CSS scroll-behavior: smooth, which makes direct
      // scrollLeft assignment animate instead of tracking the mouse in
      // real time. Suspend it for the duration of the drag, then restore.
      previousScrollBehavior = el.style.scrollBehavior;
      el.style.scrollBehavior = 'auto';

      if (el.setPointerCapture) {
        el.setPointerCapture(event.pointerId);
      }
    }

    // Only reached once actually dragging — stops native link/image drag
    // from kicking in, without ever touching a plain click.
    event.preventDefault();
    el.scrollLeft = scrollStart - distance;
  }

  function onPointerUp(event) {
    if (!isTracking) return;
    isTracking = false;

    if (!isDragging) return; // plain click — nothing to clean up or swallow

    isDragging = false;
    el.classList.remove('dragging');
    el.style.scrollBehavior = previousScrollBehavior;

    if (el.hasPointerCapture && el.hasPointerCapture(event.pointerId)) {
      el.releasePointerCapture(event.pointerId);
    }

    // A real drag happened — swallow the click that follows so a
    // dragged-past link (a logo, a product card) doesn't navigate.
    el.addEventListener(
      'click',
      (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
      },
      { capture: true, once: true }
    );
  }

  el.addEventListener('pointerdown', onPointerDown);
  el.addEventListener('pointermove', onPointerMove);
  el.addEventListener('pointerup', onPointerUp);
  el.addEventListener('pointercancel', onPointerUp);
  el.addEventListener('pointerleave', onPointerUp);
}

class DragScroll extends HTMLElement {
  connectedCallback() {
    enableDragScroll(this);

    this.scrollLeft = 0;
    window.addEventListener('pageshow', () => {
      this.scrollLeft = 0;
    });
  }
}

customElements.define('drag-scroll', DragScroll);

// Apply the same behavior to any other element explicitly opted in via
// [data-drag-scroll], regardless of its tag/custom-element type (e.g.
// <slider-element>, which can't also be a <drag-scroll> since an element
// can only have one tag name).
document.querySelectorAll('[data-drag-scroll]').forEach(enableDragScroll);